import * as constants from '@/constants'

const { v4: uuidv4 } = require('uuid')

export const messageTypes = {
  TEXT: 'text',
  FILE: 'file',
  FEEDBACK: 'feedback',
  GENERIC_TEMPLATE: 'snippet',
  VIDEO: 'video',
  IMAGE: 'image',
  QUICK_REPLY: 'quick_reply',
  CUSTOM_COMPONENT: 'component',
  SYSTEM: 'system',
  SYSTEM_NOTIFICATION: 'system_notification',
  UNKNOWN: 'unknown',
  TYPING: 'typing'
}
export const quickReplyContentTypes = {
  TEXT: 'text',
  LOCATION: 'location',
  DATE_PICKER: 'date_picker',
  LOGIN: 'login_redirect',
  EXPAND: 'expand'
}

export const attachmentTypes = {
  TEMPLATE: 'template',
  LOCATION: 'location',
  VIDEO: 'video',
  IMAGE: 'image'
}

export const templateTypes = {
  GENERIC: 'generic'
}

export const buttonTypes = {
  CALL: 'call',
  POSTBACK: 'postback',
  WEB_URL: 'weburl'
}
export class QuickReply {
  constructor(content_type, payload, title) {
    this.content_type = content_type
    this.payload = payload
    this.title = title
  }
}

export class Payload {
  /**
   *
   * @param template_type
   * @param elements
   */
  constructor(template_type, elements) {
    this.template_type = template_type
    this.elements = elements
  }
}

export class Attachment {
  /**
   *
   * @param {string} type
   * @param {?Payload} payload
   */
  constructor(type, payload) {
    this.type = type
    this.payload = payload
  }
}

export class Button {
  /**
   *
   * @param type
   * @param payload
   * @param title
   */
  constructor(type, payload, title) {
    this.type = type
    this.payload = payload
    this.title = title
  }
}

export class UrlButton {
  /**
   *
   * @param {string} url
   * @param {?string} title
   * @param {string} webview_height_ratio "<compact|tall|full>"
   * @param {?boolean} messenger_extensions
   * @param {?string} fallback_url
   */
  constructor(
    url,
    title = undefined,
    webview_height_ratio = undefined,
    messenger_extensions = undefined,
    fallback_url = undefined
  ) {
    this.type = buttonTypes.WEB_URL
    this.url = url
    this.title = title
    this.webview_height_ratio = webview_height_ratio
    this.messenger_extensions = messenger_extensions
    this.fallback_url = fallback_url
  }
}

/**
 * @see https://developers.facebook.com/docs/messenger-platform/reference/template/generic
 */
export class Element {
  /**
   *
   * @param {string} title The title to display in the template. 80 character limit
   * @param {subtitle} subtitle
   * @param {?string} image_url
   * @param {?Object} default_action The default action executed when the template is tapped. Accepts the same properties as URL button, except title.
   * @param {?Button[]} buttons
   */
  constructor(title, image_url, default_action, buttons) {
    this.title = title
    this.image_url = image_url
    this.default_action = default_action
    this.buttons = buttons
  }
}
export class Message {
  /**
   *
   * @param {string} author
   * @param {string} sessionId
   * @param {?string} text
   * @param {?string} id
   * @param {!QuickReply[]} quick_replies
   * @param {?Attachment} attachment
   * @param {Object} metadata
   * @param {string} meta
   */
  constructor(
    author,
    sessionId,
    text = undefined,
    id = undefined,
    quick_replies = [],
    attachment = undefined,
    metadata = {},
    meta = undefined
  ) {
    this.id = id || this.getNewMessageId()
    this.author = author
    this.sessionId = sessionId
    this.created = Date.now()
    this.sent = undefined
    this.received = undefined
    this.text = text
    this.payload = undefined
    this.quick_replies = quick_replies
    this.attachment = attachment
    this.metadata = metadata
    this.type = this.getType()
    this.meta = meta
  }

  getUserResponsePayload() {
    return this.payload ? this.payload : this.text
  }

  /**
   * @returns {string}
   */
  getType() {
    if (this.isSystemMessage()) {
      return messageTypes.SYSTEM
    }
    if (this.isSystemNotification()) {
      return messageTypes.SYSTEM_NOTIFICATION
    }
    if (this.isGenericTemplate()) {
      return messageTypes.GENERIC_TEMPLATE
    }
    if (this.isVideo()) {
      return messageTypes.VIDEO
    }
    if (this.isImage()) {
      return messageTypes.IMAGE
    }
    if (this.isQuickReply()) {
      return messageTypes.QUICK_REPLY
    }
    if (this.isCustomComponent()) {
      return messageTypes.CUSTOM_COMPONENT
    }
    if (this.isFile()) {
      return messageTypes.FILE
    }
    if (this.isText()) {
      return messageTypes.TEXT
    }
    return messageTypes.UNKNOWN
  }

  getNewMessageId() {
    return uuidv4()
  }

  /**
   * @returns {boolean}
   */
  isSystemMessage() {
    return (
      this.author === constants.AUTHOR_BOT &&
      this.text &&
      this.text.includes('SYSTEM_MESSAGE:')
    )
  }

  /**
   * @returns {boolean|string}
   */
  isSystemNotification() {
    return this.author === constants.AUTHOR_SYSTEM && this.text
  }

  /**
   * @returns {boolean}
   */
  isBotMessage() {
    return this.author === constants.AUTHOR_BOT
  }

  /**
   * @returns {boolean}
   */
  isOperatorMessage() {
    return this.author === constants.AUTHOR_OPERATOR
  }

  /**
   * @returns {boolean}
   */
  isGenericTemplate() {
    return (
      this.attachment &&
      this.attachment.type === attachmentTypes.TEMPLATE &&
      Object.keys(this.attachment.payload).indexOf('template_type') >= 0 &&
      this.attachment.payload.template_type === 'generic' &&
      Object.keys(this.attachment.payload).indexOf('elements') >= 0 &&
      this.attachment.payload.elements.length > 0
    )
  }

  /**
   * @returns {boolean}
   */
  isVideo() {
    return this.attachment && this.attachment.type === attachmentTypes.VIDEO
  }

  /**
   * @returns {boolean}
   */
  isImage() {
    return this.attachment && this.attachment.type === attachmentTypes.IMAGE
  }

  /**
   * @returns {boolean}
   */
  isFile() {
    return (
      this.metadata && this.metadata.files && this.metadata.files.length > 0
    )
  }

  /**
   * @returns {boolean}
   */
  isText() {
    return !!this.text
  }

  /**
   * @returns {boolean}
   */
  isQuickReply() {
    return this.text && this.quick_replies
  }

  /**
   * @returns {boolean}
   */
  isCustomComponent() {
    return false
  }
}
